import { Chip, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CloseCircleFill20Px,
  Error24Px,
  CheckmarkCircle24Px
} from "@qubit/autoparts";
import { Trans, useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import {
  selectCurrentPickQuantity,
  selectSumOfWeightsCapturedOnPickSoFar,
  selectWeightsCapturedOnPickSoFar,
  selectWeightThresholdCheckResult,
  selectWeightThresholdLeftToPick
} from "~/features/autostorePicking/autostorePicking.slice";
import { selectAbleQtyToPick } from "~/redux/selectors/outOfStockSelectors";

import { WeightThresholdDto } from "~/types/api";

import { WeightEntryModalButton } from "./WeightEntryModalButton";

type ScannedWeightChipsProps = {
  onRemoveWeight: (weightValue: number) => void;
  isRemovingWeight?: boolean;
  weightThreshold?: WeightThresholdDto;
};

export function ScannedWeightChips({
  onRemoveWeight,
  isRemovingWeight = false,
  weightThreshold
}: ScannedWeightChipsProps) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const weightsCapturedOnPickSoFar = useAppSelector(
    selectWeightsCapturedOnPickSoFar
  );
  const currentPickQuantity = useAppSelector(selectCurrentPickQuantity);
  const ableToPickQty = useAppSelector(selectAbleQtyToPick);
  const weightThresholdCheckResult = useAppSelector(
    selectWeightThresholdCheckResult
  );
  const sumOfWeightsCapturedOnPickSoFar = useAppSelector(
    selectSumOfWeightsCapturedOnPickSoFar
  );
  const weightThresholdLeftToPick = useAppSelector(
    selectWeightThresholdLeftToPick
  );

  const initialWeightMin = weightThreshold?.minimum.value;
  const initialWeightMax = weightThreshold?.maximum.value;
  const isOverweight = weightThresholdCheckResult === "AboveMaximum";
  const isUnderweight = weightThresholdCheckResult === "BelowMinimum";
  const isInWeightRange = weightThresholdCheckResult === "WithinThreshold";
  const effectivePickQuantity = ableToPickQty ?? currentPickQuantity;
  const isPartialOOS = ableToPickQty && ableToPickQty < currentPickQuantity;
  const canCaptureMoreWeights =
    effectivePickQuantity > 0 &&
    weightsCapturedOnPickSoFar.length < effectivePickQuantity;
  const isUnderweightAndAllPicked =
    isUnderweight &&
    !canCaptureMoreWeights &&
    weightsCapturedOnPickSoFar.length > 0;

  const isMidPickUnderweight = isUnderweight && canCaptureMoreWeights;
  const showThresholdWarning = isOverweight || isUnderweightAndAllPicked;
  const chipBorderColor = showThresholdWarning
    ? palette.error.dark
    : isMidPickUnderweight
      ? palette.warning.dark
      : palette.success.dark;
  const chipBackgroundColor = showThresholdWarning
    ? palette.error.light
    : isMidPickUnderweight
      ? palette.warning.light
      : palette.success.light;
  const remainingWeightMin = weightThresholdLeftToPick?.minimum.value;
  const remainingWeightMax = weightThresholdLeftToPick?.maximum.value;
  const displayWeightMin = remainingWeightMin ?? initialWeightMin;
  const displayWeightMax = remainingWeightMax ?? initialWeightMax;
  const totalWeight = sumOfWeightsCapturedOnPickSoFar?.value;
  const amountOverweight =
    isOverweight && totalWeight && initialWeightMax
      ? (totalWeight - initialWeightMax).toFixed(2)
      : 0;
  const amountShort =
    isUnderweight && totalWeight && initialWeightMin
      ? (initialWeightMin - totalWeight).toFixed(2)
      : 0;
  const isMidPickInWeightRange =
    isInWeightRange && totalWeight && totalWeight > 0;
  const units = weightThreshold?.minimum.units ?? "";
  const thresholdWarningLabel = isOverweight ? "overweight" : "underweight";
  const weightsLeftToCapture = canCaptureMoreWeights
    ? effectivePickQuantity - weightsCapturedOnPickSoFar.length
    : 0;

  const weightInfoText =
    (isOverweight || !canCaptureMoreWeights || isMidPickInWeightRange) &&
    totalWeight
      ? "total weight"
      : "weight remaining to pack";
  const placeholderWeights = new Array(weightsLeftToCapture).fill(0);

  return (
    <Stack direction="column" mt={2} mb={1} px={3}>
      {showThresholdWarning && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          justifyContent="center"
          mb={1}
        >
          <Stack direction="row" alignItems="center" gap={1} pl={0.5}>
            <Error24Px width={32} height={32} fill={palette.error.main} />
            <Typography
              fontSize="1.25rem"
              fontWeight={500}
              lineHeight={1.3}
              whiteSpace="nowrap"
            >
              {t(thresholdWarningLabel)}
            </Typography>
          </Stack>
          <Typography fontSize="1.25rem" lineHeight={1.3}>
            {t(
              isOverweight
                ? "total weight exceeds threshold instruction"
                : "total weight does not meet threshold instruction"
            )}
          </Typography>
        </Stack>
      )}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        justifyContent="center"
        alignItems="center"
        mb={1}
      >
        {weightsCapturedOnPickSoFar.map((weight, i) => (
          <Chip
            key={`${weight.value}-${i}`}
            label={`${weight.value} ${weight.units}`}
            variant="outlined"
            disabled={isRemovingWeight}
            deleteIcon={
              <CloseCircleFill20Px
                width={20}
                height={20}
                style={{ marginLeft: 0 }}
              />
            }
            onDelete={() => onRemoveWeight(weight.value)}
            style={{ height: "42px" }}
            sx={{
              borderColor: chipBorderColor,
              backgroundColor: chipBackgroundColor,
              fontSize: "1.25rem"
            }}
          />
        ))}
        {!isOverweight && canCaptureMoreWeights && <WeightEntryModalButton />}
        {placeholderWeights.map((_, i) => (
          <Chip
            key={`placeholder-${i}`}
            label={`0.00 ${units}`}
            variant="outlined"
            disabled={true}
            deleteIcon={
              <CloseCircleFill20Px
                width={20}
                height={20}
                style={{ marginLeft: 0 }}
              />
            }
            style={{ height: "42px" }}
            sx={{
              fontSize: "1.25rem"
            }}
          />
        ))}
      </Stack>
      <Stack direction="column" alignItems="center">
        {!isPartialOOS && (isOverweight || isUnderweightAndAllPicked) && (
          <Typography
            fontSize="1.25rem"
            color={palette.error.dark}
            textAlign="center"
          >
            <Trans
              i18nKey={
                isOverweight ? "weight amount over" : "weight amount short"
              }
              values={{
                amount: isOverweight ? amountOverweight : amountShort,
                units
              }}
            />
          </Typography>
        )}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          gap={1}
        >
          {!canCaptureMoreWeights && isInWeightRange && (
            <CheckmarkCircle24Px fill={palette.success.dark} />
          )}
          {displayWeightMin !== undefined &&
            !isOverweight &&
            !isUnderweightAndAllPicked && (
              <Typography fontSize="1.25rem">
                <Trans
                  i18nKey={weightInfoText}
                  values={{
                    min: displayWeightMin.toFixed(2),
                    max: displayWeightMax?.toFixed(2),
                    units,
                    total: totalWeight?.toFixed(2)
                  }}
                  components={{
                    bold: (
                      <Typography
                        component="span"
                        fontSize="inherit"
                        fontWeight="bold"
                      />
                    ),
                    muted: (
                      <Typography
                        component="span"
                        fontSize="inherit"
                        color={palette.text.secondary}
                      />
                    )
                  }}
                />
              </Typography>
            )}
        </Stack>
        {!isPartialOOS &&
          initialWeightMin &&
          initialWeightMax &&
          totalWeight && (
            <Stack>
              <Typography fontSize="1.25rem" color={palette.text.secondary}>
                <Trans
                  i18nKey={"target weight info"}
                  values={{
                    min: initialWeightMin.toFixed(2),
                    max: initialWeightMax.toFixed(2),
                    units,
                    total: totalWeight?.toFixed(2)
                  }}
                />
              </Typography>
            </Stack>
          )}
      </Stack>
    </Stack>
  );
}
